// src/pages/aideskmanager.js
import React from "react";
import { Helmet } from "react-helmet";

export default function AiDeskManager() {
  return (
    <React.Fragment>
      <Helmet>
        <title>AI Desk Manager — Try It Live</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300;0,400;0,500;0,700;0,800;0,900;1,300&family=Space+Grotesk:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      {/* 1 — HERO  |  NO BUTTONS*/}
      <section className="adm-hero">
        <div className="adm-hero-tag">
          <span></span>
          Live System — Experience It Before You Decide
        </div>

        <h1>
          Your AI Voice Employee That
          <br />
          <em>Answers, Qualifies &amp; Books</em>
          <br />
          Customers 24/7
        </h1>

        <p className="adm-hero-sub">
          Let visitors experience your AI through a live voice call before you decide — no setup required on your end.
        </p>

        <div className="adm-hero-notice">
          <p>
            This is a <strong>live system demo.</strong> You can speak with the AI for 5 minutes, hear how it handles your callers, qualifies leads, and books appointments. <strong>If it feels right, you continue.</strong> Zero risk, zero obligation.
          </p>
        </div>

        <a href="tel:+15807489531" className="adm-hero-live">
          <span className="adm-live-dot"></span>
          <div>
            <div className="adm-live-text">Try Live AI Desk Manager</div>
            <div className="adm-live-small">Active — listening now</div>
          </div>
          <div className="adm-bars">
            <div className="adm-b"></div>
            <div className="adm-b"></div>
            <div className="adm-b"></div>
            <div className="adm-b"></div>
            <div className="adm-b"></div>
            <div className="adm-b"></div>
            <div className="adm-b"></div>
          </div>
        </a>

        <div className="adm-scroll-hint">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
          Scroll to experience
        </div>
      </section>


       {/* 2 — VIDEO */}
       <section
  className="adm-vid-section"
  ref={(el) => {
    if (!el || el._admInit) return;
    el._admInit = true;

    const vid     = el.querySelector('#admVid');
    const pp      = el.querySelector('#admPP');
    const playIco = el.querySelector('#admPI');
    const pauseIco= el.querySelector('#admPaI');
    const soundBtn= el.querySelector('#admSound');
    const soundOn = el.querySelector('#admSoundOn');
    const soundOff= el.querySelector('#admSoundOff');
    const progWrap= el.querySelector('#admPW');
    const progFill= el.querySelector('#admPF');
    const timeTxt = el.querySelector('#admTT');

    let triedAutoUnmute = false;

    // autoplay on scroll into view, try to unmute automatically
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          vid.play().catch(() => {});

          // attempt auto-unmute the first time it scrolls into view
          if (!triedAutoUnmute) {
            triedAutoUnmute = true;
            vid.muted = false;
            vid.volume = 1;
            vid.play().then(() => {
              syncSound();
            }).catch(() => {
              // browser blocked unmuted autoplay — fall back to muted
              vid.muted = true;
              vid.play().catch(() => {});
              syncSound();
            });
          }
        } else {
          vid.pause();
        }
      });
    }, { threshold: 0.4 });
    observer.observe(el);

    const fmt = (s) => {
      const m = Math.floor(s / 60), sec = Math.floor(s % 60);
      return m + ':' + (sec < 10 ? '0' : '') + sec;
    };

    const syncPlay = () => {
      playIco.style.display  = vid.paused ? '' : 'none';
      pauseIco.style.display = vid.paused ? 'none' : '';
    };

    const syncSound = () => {
      const muted = vid.muted || vid.volume === 0;
      soundOn.style.display  = muted ? '' : 'none';
      soundOff.style.display = muted ? 'none' : '';
      soundBtn.setAttribute('aria-label', muted ? 'Turn sound on' : 'Mute');
    };

    pp.onclick = () => { vid.paused ? vid.play() : vid.pause(); };
    vid.onclick = () => { vid.paused ? vid.play() : vid.pause(); };

    soundBtn.onclick = () => {
      vid.muted = !vid.muted;
      if (!vid.muted && vid.volume === 0) vid.volume = 1;
      syncSound();
    };

    vid.addEventListener('play',  syncPlay);
    vid.addEventListener('pause', syncPlay);
    vid.addEventListener('volumechange', syncSound);

    vid.addEventListener('timeupdate', () => {
      if (!vid.duration) return;
      progFill.style.width = (vid.currentTime / vid.duration * 100) + '%';
      timeTxt.textContent  = fmt(vid.currentTime);
    });

    progWrap.addEventListener('click', (e) => {
      const r = progWrap.getBoundingClientRect();
      vid.currentTime = ((e.clientX - r.left) / r.width) * vid.duration;
    });

    // loop instead of stopping at end
    vid.addEventListener('ended', () => {
      vid.currentTime = 0;
      vid.play().catch(() => {});
    });
  }}
>
  <video
    id="admVid"
    src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/videos/ai%20desk%20video.mp4"
    muted
    loop
    playsInline
    preload="auto"
    className="adm-vid-el"
  />

  {/* Sound button — top right corner */}
  <button className="adm-sound-btn" id="admSound" aria-label="Turn sound on">
    {/* Sound ON (muted state) */}
    <svg id="admSoundOn" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
      <line x1="23" y1="9" x2="17" y2="15"/>
      <line x1="17" y1="9" x2="23" y2="15"/>
    </svg>
    <span id="admSoundOnTxt" style={{fontSize:'12px',fontWeight:600,letterSpacing:'0.04em'}}>SOUND ON</span>
    {/* Sound OFF (unmuted state) */}
    <svg id="admSoundOff" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'none'}}>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
    </svg>
  </button>

  {/* Bottom controls bar */}
  <div className="adm-vid-controls">
    <button className="adm-vid-btn" id="admPP" aria-label="Play/Pause">
      <svg id="admPI" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <polygon points="5 3 19 12 5 21 5 3"/>
      </svg>
      <svg id="admPaI" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{display:'none'}}>
        <rect x="6" y="4" width="4" height="16" rx="1"/>
        <rect x="14" y="4" width="4" height="16" rx="1"/>
      </svg>
    </button>

    <div className="adm-vid-prog" id="admPW">
      <div className="adm-vid-prog-bg">
        <div className="adm-vid-prog-fill" id="admPF"/>
      </div>
    </div>

    <span className="adm-vid-time" id="admTT">0:00</span>
  </div>

  <style>{`
    .adm-vid-section {
      width: 100%;
      background: #000;
      position: relative;
      overflow: hidden;
    }
    .adm-vid-el {
      width: 100%;
      display: block;
      max-height: 85vh;
      object-fit: cover;
      cursor: pointer;
    }
    /* Sound button — overlaid top right */
    .adm-sound-btn {
      position: absolute;
      top: 18px; right: 18px;
      display: flex; align-items: center; gap: 7px;
      background: rgba(0,0,0,0.55);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      border: 1px solid rgba(255,255,255,0.18);
      border-radius: 100px;
      color: #fff;
      padding: 7px 14px 7px 10px;
      cursor: pointer;
      font-family: var(--adm-fb);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      transition: background .18s, border-color .18s;
      z-index: 10;
    }
    .adm-sound-btn:hover {
      background: rgba(0,0,0,0.78);
      border-color: rgba(255,255,255,0.35);
    }
    /* Bottom controls */
    .adm-vid-controls {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 16px;
      background: rgba(10,10,12,0.95);
    }
    .adm-vid-btn {
      background: none; border: none; color: #fff;
      padding: 5px; cursor: pointer; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      border-radius: 5px;
    }
    .adm-vid-btn:hover { background: rgba(255,255,255,.1); }
    .adm-vid-prog {
      flex: 1; cursor: pointer; padding: 8px 0;
    }
    .adm-vid-prog-bg {
      width: 100%; height: 4px;
      background: rgba(255,255,255,.2);
      border-radius: 2px; overflow: hidden;
    }
    .adm-vid-prog-fill {
      height: 100%; width: 0%;
      background: #E05820;
      border-radius: 2px;
      transition: width .1s linear;
    }
    .adm-vid-time {
      font-size: 12px;
      color: rgba(255,255,255,.6);
      font-variant-numeric: tabular-nums;
      min-width: 32px; flex-shrink: 0;
      font-family: var(--adm-fb);
    }
  `}</style>
       </section>


      {/* 3 — DEMO  |  NO BUTTONS */}
      <section className="adm-demo">
        <div className="adm-demo-inner">
          <div className="adm-eyebrow">The Demo Experience</div>
          <h2 className="adm-demo-h2">
            Experience Your AI Employee
            <br />
            <em>in Real Time</em>
          </h2>

          <div className="adm-call-flow">
            <div className="adm-call-step">
              <div className="adm-cs-n">01</div>
              <div className="adm-cs-body">
                <div className="adm-cs-title">You click the button below</div>
                <div className="adm-cs-text">The AI connects within seconds. No forms. No waiting. No setup on your end.</div>
              </div>
            </div>
            <div className="adm-call-step">
              <div className="adm-cs-n">02</div>
              <div className="adm-cs-body">
                <div className="adm-cs-title">The AI speaks — naturally</div>
                <div className="adm-cs-text">It responds like a trained receptionist. Warm tone, clear language, handles objections in real time.</div>
              </div>
            </div>
            <div className="adm-call-step">
              <div className="adm-cs-n">03</div>
              <div className="adm-cs-body">
                <div className="adm-cs-title">It qualifies, answers, and books</div>
                <div className="adm-cs-text">Budget, urgency, intent — captured automatically. FAQs answered live. Appointments booked directly into your calendar.</div>
              </div>
            </div>
            <div className="adm-call-step">
              <div className="adm-cs-n">04</div>
              <div className="adm-cs-body">
                <div className="adm-cs-title">You see everything — in real time</div>
                <div className="adm-cs-text">Call summary sent by SMS and email the moment it ends. Full CRM log updated automatically. Nothing falls through.</div>
              </div>
            </div>
          </div>

          <div className="adm-caps">
            <div className="adm-cap">24/7 call answering</div>
            <div className="adm-cap">Lead qualification</div>
            <div className="adm-cap">FAQ handling</div>
            <div className="adm-cap">Appointment booking</div>
            <div className="adm-cap">CRM sync</div>
            <div className="adm-cap">Missed call recovery</div>
          </div>

          <div className="adm-demo-line">
            No missed calls. No lost leads.
            <br />
            <em>No manual follow-up.</em>
          </div>
        </div>
      </section>

      {/* 4 — MARKET + SCARCITY + BONUSES  |  NO BUTTONS */}
      <section className="adm-market">
        <div className="adm-market-inner">
          <div className="adm-eyebrow">2026 Market Reality</div>
          <h2 className="adm-market-h2">
            AI Voice Automation Is Now
            <br />
            the Standard for <em>High-Performing</em> Businesses
          </h2>

          <div className="adm-truths">
            <div className="adm-truth">
              <div className="adm-truth-dash">—</div>
              <div className="adm-truth-text">AI voice systems are rapidly becoming the default in service industries — early adopters are compounding the advantage.</div>
            </div>
            <div className="adm-truth">
              <div className="adm-truth-dash">—</div>
              <div className="adm-truth-text">Businesses that respond within 60 seconds close significantly more deals than those relying on manual follow-up.</div>
            </div>
            <div className="adm-truth">
              <div className="adm-truth-dash">—</div>
              <div className="adm-truth-text">
                <strong>Speed is now the difference between winning and losing a customer.</strong> Every missed call is a deal closed by your competitor.
              </div>
            </div>
          </div>

          <div className="adm-market-sub">Once installed, the system handles</div>
          <div className="adm-feat-list">
            <div className="adm-feat"><div className="adm-feat-dot adm-on"></div>All inbound calls — 24/7, no exceptions</div>
            <div className="adm-feat"><div className="adm-feat-dot adm-on"></div>FAQs handled like a trained human agent</div>
            <div className="adm-feat"><div className="adm-feat-dot adm-on"></div>Lead qualification — budget, urgency, intent captured automatically</div>
            <div className="adm-feat"><div className="adm-feat-dot adm-on"></div>Appointments booked directly into your calendar</div>
            <div className="adm-feat"><div className="adm-feat-dot adm-on"></div>High-value leads routed to you immediately</div>
            <div className="adm-feat"><div className="adm-feat-dot adm-on"></div>Missed calls recovered by automated SMS within minutes</div>
          </div>

          {/* Scarcity */}
          <div className="adm-scarcity">
            <div className="adm-scarcity-head">Limited Onboarding Availability</div>
            <div className="adm-scarcity-items">
              <div className="adm-scarcity-item">
                <div className="adm-si-icon">⏳</div>
                We only onboard a limited number of businesses per region each month.
              </div>
              <div className="adm-scarcity-item">
                <div className="adm-si-icon">🔒</div>
                Setup capacity is restricted due to onboarding quality standards — each business receives individual configuration.
              </div>
              <div className="adm-scarcity-item">
                <div className="adm-si-icon">📅</div>
                This month's access window is currently open. Once slots fill, the next cohort opens in 30 days.
              </div>
            </div>
          </div>

          {/* Bonus stack */}
          <div className="adm-bonuses">
            <div className="adm-bonus-head">When You Activate Today, You Also Get</div>
            <p className="adm-bonus-intro">These bonuses are included free for clients who start this month — not available after the cohort closes.</p>
            <div className="adm-bonus-list">
              <div className="adm-bonus">
                <div className="adm-bonus-icon">🎁</div>
                <div className="adm-bonus-body">
                  <div className="adm-bonus-title">Custom High-Converting Website</div>
                  <div className="adm-bonus-desc">A fully built, SEO-optimised website designed to attract 20–30 daily inbound leads — built specifically for your business and offer.</div>
                  <span className="adm-bonus-badge">Delivered within 24 hours</span>
                </div>
              </div>
              <div className="adm-bonus">
                <div className="adm-bonus-icon">⚡</div>
                <div className="adm-bonus-body">
                  <div className="adm-bonus-title">Design Mockups — Within 1–2 Hours</div>
                  <div className="adm-bonus-desc">See exactly how your website will look before it's built. Approve, adjust, or request changes — all before we go live.</div>
                  <span className="adm-bonus-badge">Same-day delivery</span>
                </div>
              </div>
              <div className="adm-bonus">
                <div className="adm-bonus-icon">📈</div>
                <div className="adm-bonus-body">
                  <div className="adm-bonus-title">Conversion-Optimised Layout</div>
                  <div className="adm-bonus-desc">Every page element is built around one goal — turning visitors into booked calls. Not a template. Built for your market.</div>
                  <span className="adm-bonus-badge">Included free</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
 

      {/* 5 — PROMO VIDEO (muted, autoplay, loop) */}
      <section className="adm-promo-vid-section">
  <video
    src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/videos/ai%20desk%20manager%20product%20promo.mp4"
    autoPlay
    muted
    loop
    playsInline
    preload="auto"
    className="adm-promo-vid-el"
  />

  <style>{`
    .adm-promo-vid-section {
      width: 100%;
      background: #000;
      position: relative;
      overflow: hidden;
    }
    .adm-promo-vid-el {
      width: 100%;
      display: block;
      height: auto;
      max-height: none;
      object-fit: contain;
    }
  `}</style>
      </section>


      {/* 6 — PRICING  |  btn-pay APPEARS HERE ONLY*/}
      <section className="adm-pricing">
        <div className="adm-eyebrow adm-pricing-eyebrow">Final Decision</div>

        <h2 className="adm-pricing-h2">
          Simple Pricing.
          <br />
          One Plan. Full AI System.
        </h2>

        <div className="adm-price-block">
          <div className="adm-price-figure">
            <span className="adm-price-sym">$</span>
            <span className="adm-price-num">297</span>
            <span className="adm-price-mo">/ month</span>
          </div>
        </div>

        <div className="adm-price-setup">One-time setup fee: $450</div>

        <div className="adm-inc-label">Everything included</div>
        <div className="adm-inc-tags">
          <span className="adm-inc-tag">AI Voice Call System</span>
          <span className="adm-inc-tag">Lead Qualification Engine</span>
          <span className="adm-inc-tag">Appointment Booking</span>
          <span className="adm-inc-tag">FAQ Handling AI</span>
          <span className="adm-inc-tag">Full Automation Setup</span>
          <span className="adm-inc-tag">Custom Website Bonus</span>
        </div>

        {/* BUTTON — ONLY HERE */}
        <div className="adm-pay-cta">
          <a href="https://links.equiptbiz.com/payment-link/6a3ea32f9b12592b36824b0a" target="_blank" rel="noopener noreferrer" className="adm-btn-pay">
            Pay &amp; Activate — $297/month
          </a>
          <p className="adm-pay-sub">Full system access + CRM integration included</p>
        </div>

        <div className="adm-urgency-lines">
          <span>Setup takes less than 10 minutes</span>
          <span>Limited onboarding availability per region</span>
        </div>

        <div className="adm-trust-final">
          Your AI system can go live
          <br />
          <em>immediately after activation.</em>
        </div>
      </section>
      

      <style>{`
        /* ── RESET + BASE (scoped to this page) ────────────────────────────────────── */
        .adm-hero, .adm-demo, .adm-market, .adm-pricing {
          box-sizing: border-box;
        }
        .adm-hero *, .adm-demo *, .adm-market *, .adm-pricing * {
          box-sizing: border-box;
        }

        :root {
          --adm-w:    #FFFFFF;
          --adm-off:  #F5F5F6;
          --adm-g1:   #EAEAEC;
          --adm-g2:   #C6C6CA;
          --adm-g3:   #8C8C94;
          --adm-g4:   #52525A;
          --adm-ink:  #18181C;
          --adm-blk:  #0C0C0F;
          --adm-org:  #E05820;
          --adm-orl:  #FF6B30;
          --adm-obg:  #FEF0E8;
          --adm-fd:   'Space Grotesk', sans-serif;
          --adm-fb:   'Inter', sans-serif;
        }

        .adm-hero, .adm-demo, .adm-market, .adm-pricing {
          font-family: var(--adm-fb);
          color: var(--adm-ink);
          -webkit-font-smoothing: antialiased;
        }

        /* ── BUTTONS ── */
        .adm-btn-pay {
          display: inline-flex; align-items: center; justify-content: center; gap: 11px;
          background: var(--adm-org);
          color: #fff;
          border: none; border-radius: 100px;
          font-family: var(--adm-fb); font-size: 17px; font-weight: 700;
          padding: 18px 44px;
          cursor: pointer; text-decoration: none;
          letter-spacing: -0.2px;
          box-shadow: 0 6px 32px rgba(224,88,32,0.40);
          transition: background .18s, transform .15s, box-shadow .18s;
        }
        .adm-btn-pay:hover { background: var(--adm-orl); transform: translateY(-2px); box-shadow: 0 12px 40px rgba(224,88,32,0.55); }

        /* ── SHARED TYPOGRAPHY ───────────────────────────────── */
        .adm-eyebrow {
          font-size: 11px; font-weight: 700; letter-spacing: 0.14em;
          text-transform: uppercase; color: var(--adm-org);
          display: flex; align-items: center; gap: 8px;
        }
        .adm-eyebrow::before {
          content: ''; width: 6px; height: 6px;
          background: var(--adm-org); border-radius: 50%;
          animation: adm-pulse 2s ease-in-out infinite;
        }
        @keyframes adm-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }

        /* ─────────────────────────────────────────────────────
           SECTION 1 — HERO
        ───────────────────────────────────────────────────── */
        .adm-hero {
          min-height: 100vh;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          padding: 120px 56px 100px;
          text-align: center;
          background: var(--adm-w);
          position: relative; overflow: hidden;
        }

        .adm-hero::before {
          content: '';
          position: absolute; top: -80px; left: 50%; transform: translateX(-50%);
          width: 960px; height: 560px;
          background: radial-gradient(ellipse at top,
            rgba(224,88,32,.07) 0%, transparent 62%);
          pointer-events: none;
        }

        .adm-hero-tag {
          background: var(--adm-obg); border-radius: 100px;
          padding: 6px 18px; margin-bottom: 44px;
          font-size: 12px; font-weight: 700;
          letter-spacing: 0.06em; color: var(--adm-org);
          display: inline-flex; align-items: center; gap: 8px;
          position: relative; z-index: 1;
        }
        .adm-hero-tag span {
          width: 7px; height: 7px; background: var(--adm-org);
          border-radius: 50%; animation: adm-pulse 1.8s ease-in-out infinite;
        }

        .adm-hero h1 {
          font-family: var(--adm-fd);
          font-size: clamp(44px, 7vw, 88px);
          font-weight: 700; line-height: 1.02;
          letter-spacing: -3px; color: var(--adm-blk);
          max-width: 900px; margin: 0 auto 32px;
          position: relative; z-index: 1;
        }
        .adm-hero h1 em { font-style: normal; color: var(--adm-org); }

        .adm-hero-sub {
          font-size: clamp(18px, 2.4vw, 22px);
          font-weight: 300; color: var(--adm-g3);
          max-width: 560px; margin: 0 auto 40px;
          line-height: 1.62; letter-spacing: -.2px;
          position: relative; z-index: 1;
        }

        .adm-hero-notice {
          max-width: 500px; margin: 0 auto;
          background: var(--adm-off); border-radius: 14px;
          padding: 22px 28px;
          border: 1px solid var(--adm-g1);
          position: relative; z-index: 1;
        }
        .adm-hero-notice p {
          font-size: 14px; color: var(--adm-g4); line-height: 1.7;
          letter-spacing: -.1px; margin: 0;
        }
        .adm-hero-notice p strong { color: var(--adm-ink); font-weight: 600; }

        .adm-hero-live {
          margin-top: 72px;
          display: inline-flex; align-items: center; gap: 18px;
          background: var(--adm-blk); border-radius: 100px;
          padding: 14px 30px;
          box-shadow: 0 20px 60px rgba(0,0,0,.18);
          position: relative; z-index: 1;
          text-decoration: none;
          cursor: pointer;
          transition: transform .18s, box-shadow .18s;
        }
        .adm-hero-live:hover {
          transform: translateY(-2px);
          box-shadow: 0 28px 70px rgba(0,0,0,.26);
        }
        .adm-live-dot {
          width: 8px; height: 8px; background: #30D158; border-radius: 50%;
          box-shadow: 0 0 0 3px rgba(48,209,88,.18);
          animation: adm-pulse 1.8s ease-in-out infinite;
        }
        .adm-live-text {
          font-family: var(--adm-fd); font-size: 14px; font-weight: 600;
          color: #fff; letter-spacing: -.2px;
        }
        .adm-live-small { font-size: 11px; color: rgba(255,255,255,.55); margin-top: 1px; }
        .adm-bars { display: flex; align-items: center; gap: 3px; }
        .adm-b {
          width: 3px; border-radius: 2px; background: var(--adm-org);
          animation: adm-wave 1.1s ease-in-out infinite;
        }
        .adm-b:nth-child(1){height:8px; animation-delay:0s}
        .adm-b:nth-child(2){height:20px;animation-delay:.11s}
        .adm-b:nth-child(3){height:12px;animation-delay:.22s}
        .adm-b:nth-child(4){height:26px;animation-delay:.07s}
        .adm-b:nth-child(5){height:14px;animation-delay:.18s}
        .adm-b:nth-child(6){height:18px;animation-delay:.03s}
        .adm-b:nth-child(7){height:9px; animation-delay:.26s}
        @keyframes adm-wave{0%,100%{transform:scaleY(.3);opacity:.4}50%{transform:scaleY(1);opacity:1}}

        .adm-scroll-hint {
          position: absolute; bottom: 40px; left: 50%; transform: translateX(-50%);
          display: flex; flex-direction: column; align-items: center; gap: 8px;
          font-size: 11px; color: var(--adm-g2); letter-spacing: .06em; text-transform: uppercase;
          animation: adm-float 3s ease-in-out infinite;
        }
        .adm-scroll-hint svg { color: var(--adm-g2); }
        @keyframes adm-float{0%,100%{transform:translateX(-50%) translateY(0)}50%{transform:translateX(-50%) translateY(6px)}}

        /* ─────────────────────────────────────────────────────
           SECTION 2 — DEMO
        ───────────────────────────────────────────────────── */
        .adm-demo {
          padding: 130px 56px;
          background: var(--adm-off);
        }

        .adm-demo-inner { max-width: 780px; margin: 0 auto; }

        .adm-demo-h2 {
          font-family: var(--adm-fd);
          font-size: clamp(32px, 5vw, 58px);
          font-weight: 700; line-height: 1.06;
          letter-spacing: -1.8px; color: var(--adm-blk);
          margin: 18px 0 48px;
        }
        .adm-demo-h2 em { font-style: normal; color: var(--adm-org); }

        .adm-call-flow {
          display: flex; flex-direction: column; gap: 0;
          margin-bottom: 56px;
        }

        .adm-call-step {
          display: grid; grid-template-columns: 56px 1fr;
          gap: 0; padding: 26px 0;
          border-bottom: 1px solid var(--adm-g1);
        }
        .adm-call-step:last-child { border-bottom: none; }

        .adm-cs-n {
          font-family: var(--adm-fd); font-size: 11px; font-weight: 700;
          color: var(--adm-org); letter-spacing: .08em; padding-top: 4px;
        }
        .adm-cs-title {
          font-family: var(--adm-fd); font-size: 18px; font-weight: 700;
          color: var(--adm-blk); letter-spacing: -.4px; margin-bottom: 7px;
        }
        .adm-cs-text { font-size: 14px; color: var(--adm-g3); line-height: 1.65; }

        .adm-caps {
          display: flex; flex-wrap: wrap; gap: 10px;
          margin-bottom: 52px;
        }
        .adm-cap {
          display: flex; align-items: center; gap: 8px;
          background: var(--adm-w); border: 1px solid var(--adm-g1);
          border-radius: 100px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; color: var(--adm-g4);
        }
        .adm-cap::before {
          content: ''; width: 6px; height: 6px;
          background: var(--adm-org); border-radius: 50%; flex-shrink: 0;
        }

        .adm-demo-line {
          font-family: var(--adm-fd);
          font-size: clamp(20px, 3vw, 32px); font-weight: 700;
          letter-spacing: -1px; color: var(--adm-blk);
          line-height: 1.25; margin-bottom: 0;
        }
        .adm-demo-line em { font-style: normal; color: var(--adm-org); }

        /* ─────────────────────────────────────────────────────
           SECTION 3 — MARKET SHIFT + SCARCITY + BONUSES
        ───────────────────────────────────────────────────── */
        .adm-market {
          padding: 130px 56px;
          background: var(--adm-w);
        }
        .adm-market-inner { max-width: 780px; margin: 0 auto; }

        .adm-market-h2 {
          font-family: var(--adm-fd);
          font-size: clamp(30px, 4.5vw, 52px); font-weight: 700;
          letter-spacing: -1.6px; line-height: 1.08; color: var(--adm-blk);
          margin: 18px 0 52px;
        }
        .adm-market-h2 em { font-style: normal; color: var(--adm-org); }

        .adm-truths {
          display: flex; flex-direction: column; gap: 0; margin-bottom: 64px;
        }
        .adm-truth {
          display: flex; gap: 20px; padding: 22px 0;
          border-bottom: 1px solid var(--adm-g1);
        }
        .adm-truth:first-child { border-top: 1px solid var(--adm-g1); }
        .adm-truth-dash { color: var(--adm-org); font-size: 18px; font-weight: 700; flex-shrink: 0; padding-top: 2px; }
        .adm-truth-text { font-family: var(--adm-fd); font-size: 17px; font-weight: 500; color: var(--adm-ink); line-height: 1.5; letter-spacing: -.3px; }

        .adm-market-sub {
          font-size: 11px; font-weight: 700; letter-spacing: .12em;
          text-transform: uppercase; color: var(--adm-g2); margin-bottom: 20px;
        }
        .adm-feat-list {
          display: flex; flex-direction: column; gap: 0; margin-bottom: 64px;
        }
        .adm-feat {
          display: flex; align-items: center; gap: 14px;
          padding: 16px 0; border-bottom: 1px solid var(--adm-g1);
          font-size: 15px; color: var(--adm-g4);
        }
        .adm-feat:last-child { border-bottom: none; }
        .adm-feat-dot {
          width: 8px; height: 8px; background: var(--adm-g1);
          border-radius: 50%; flex-shrink: 0;
        }
        .adm-feat-dot.adm-on { background: var(--adm-org); }

        .adm-scarcity {
          background: var(--adm-off); border-radius: 18px;
          padding: 36px 40px; margin-bottom: 64px;
          border: 1px solid var(--adm-g1);
        }
        .adm-scarcity-head {
          font-family: var(--adm-fd); font-size: 20px; font-weight: 700;
          color: var(--adm-blk); letter-spacing: -.5px; margin-bottom: 18px;
        }
        .adm-scarcity-items {
          display: flex; flex-direction: column; gap: 12px;
        }
        .adm-scarcity-item {
          display: flex; gap: 12px; align-items: flex-start;
          font-size: 14px; color: var(--adm-g4); line-height: 1.6;
        }
        .adm-si-icon {
          width: 20px; height: 20px; flex-shrink: 0;
          background: var(--adm-obg); border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          margin-top: 1px;
          font-size: 10px;
        }

        .adm-bonuses { margin-bottom: 0; }
        .adm-bonus-head {
          font-family: var(--adm-fd); font-size: 22px; font-weight: 700;
          color: var(--adm-blk); letter-spacing: -.5px; margin-bottom: 6px;
        }
        .adm-bonus-intro {
          font-size: 14px; color: var(--adm-g3); margin-bottom: 28px; line-height: 1.6;
        }
        .adm-bonus-list { display: flex; flex-direction: column; gap: 0; }
        .adm-bonus {
          display: grid; grid-template-columns: 40px 1fr;
          gap: 0; padding: 22px 0;
          border-bottom: 1px solid var(--adm-g1);
        }
        .adm-bonus:first-child { border-top: 1px solid var(--adm-g1); }
        .adm-bonus-icon { font-size: 20px; padding-top: 2px; }
        .adm-bonus-title {
          font-family: var(--adm-fd); font-size: 16px; font-weight: 700;
          color: var(--adm-blk); letter-spacing: -.3px; margin-bottom: 4px;
        }
        .adm-bonus-desc { font-size: 13px; color: var(--adm-g3); line-height: 1.6; }
        .adm-bonus-badge {
          display: inline-block; margin-top: 6px;
          background: var(--adm-obg); color: var(--adm-org);
          font-size: 11px; font-weight: 700; letter-spacing: .04em;
          padding: 3px 10px; border-radius: 100px;
        }

        /* ─────────────────────────────────────────────────────
           SECTION 4 — PRICING
        ───────────────────────────────────────────────────── */
        .adm-pricing {
          padding: 130px 56px 120px;
          background: var(--adm-blk);
          text-align: center;
          position: relative; overflow: hidden;
        }

        .adm-pricing::before {
          content: '';
          position: absolute; top: -140px; left: 50%; transform: translateX(-50%);
          width: 900px; height: 600px;
          background: radial-gradient(ellipse,
            rgba(224,88,32,.11) 0%, transparent 62%);
          pointer-events: none;
        }

        .adm-pricing-eyebrow { color: rgba(255,255,255,.5); position: relative; z-index: 1; justify-content: center; }
        .adm-pricing-eyebrow::before { background: rgba(255,255,255,.5); animation: none; }

        .adm-pricing-h2 {
          font-family: var(--adm-fd);
          font-size: clamp(28px, 4.5vw, 50px); font-weight: 700;
          letter-spacing: -1.4px; line-height: 1.1; color: #fff;
          margin: 20px auto 72px; max-width: 580px;
          position: relative; z-index: 1;
        }

        .adm-price-block { margin-bottom: 52px; position: relative; z-index: 1; }

        .adm-price-figure {
          font-family: var(--adm-fd);
          line-height: .9; letter-spacing: -6px; color: #fff;
          display: inline-flex; align-items: flex-start; gap: 0;
        }
        .adm-price-sym {
          font-size: clamp(36px, 5vw, 54px); font-weight: 700;
          color: rgba(255,255,255,.6); letter-spacing: 0;
          margin-top: 16px;
        }
        .adm-price-num {
          font-size: clamp(100px, 16vw, 160px); font-weight: 700;
          color: #fff;
        }
        .adm-price-mo {
          font-size: clamp(16px, 2vw, 22px); font-weight: 300;
          color: rgba(255,255,255,.6); letter-spacing: 0;
          align-self: flex-end; padding-bottom: 18px; margin-left: 6px;
        }

        .adm-price-setup {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(224,88,32,.12);
          border: 1px solid rgba(224,88,32,.22);
          color: rgba(255,107,48,.88);
          font-size: 13px; font-weight: 600;
          padding: 8px 20px; border-radius: 100px;
          margin-bottom: 60px; letter-spacing: -.1px;
          position: relative; z-index: 1;
        }

        .adm-inc-label {
          font-size: 11px; font-weight: 700; letter-spacing: .12em;
          text-transform: uppercase; color: rgba(255,255,255,.55);
          margin-bottom: 20px;
          position: relative; z-index: 1;
        }
        .adm-inc-tags {
          display: flex; flex-wrap: wrap; justify-content: center;
          gap: 8px; margin-bottom: 72px;
          position: relative; z-index: 1;
        }
        .adm-inc-tag {
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.18);
          color: rgba(255,255,255,.75);
          font-size: 12px; font-weight: 500;
          padding: 8px 18px; border-radius: 100px; letter-spacing: -.1px;
        }

        .adm-pay-cta { display: flex; flex-direction: column; align-items: center; gap: 10px; position: relative; z-index: 1; }
        .adm-pay-cta .adm-btn-pay { font-size: 17px; padding: 20px 52px; }

        .adm-pay-sub {
          font-size: 12px; color: rgba(255,255,255,.55);
          margin-top: 2px; letter-spacing: 0;
        }

        .adm-urgency-lines {
          margin-top: 48px;
          display: flex; flex-direction: column; gap: 8px;
          align-items: center;
          position: relative; z-index: 1;
        }
        .adm-urgency-lines span {
          font-size: 12px; color: rgba(255,255,255,.55);
          display: flex; align-items: center; gap: 6px;
        }
        .adm-urgency-lines span::before {
          content: ''; width: 4px; height: 4px;
          background: rgba(255,255,255,.55); border-radius: 50%;
        }

        .adm-trust-final {
          margin-top: 52px; padding-top: 40px;
          border-top: 1px solid rgba(255,255,255,.12);
          font-family: var(--adm-fd);
          font-size: clamp(16px, 2.5vw, 24px); font-weight: 700;
          color: rgba(255,255,255,.6); letter-spacing: -.5px;
          position: relative; z-index: 1;
        }
        .adm-trust-final em { font-style: normal; color: #fff; }



        /* ── RESPONSIVE ───────────────────────────────────────── */
        @media (max-width: 860px) {
          .adm-hero, .adm-demo, .adm-market, .adm-pricing { padding-left: 28px; padding-right: 28px; }
        }
        @media (max-width: 540px) {
          .adm-hero h1 { letter-spacing: -2px; }
          .adm-btn-pay { width: 100%; text-align: center; }
          .adm-hero-live { width: calc(100% - 40px); justify-content: center; }
          .adm-price-figure { letter-spacing: -4px; }
          .adm-price-num { font-size: 88px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .adm-b, .adm-live-dot, .adm-eyebrow::before,
          .adm-hero-tag span, .adm-scroll-hint { animation: none !important; }
          .adm-hero *, .adm-demo *, .adm-market *, .adm-pricing * { transition: none !important; }
        }
      `}</style>
    </React.Fragment>
  );
}